import {
  booleanAttribute,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BREAKPOINTS, BREAKPOINTS_PX } from '@vendasta/galaxy/constants';

@Component({
  selector: 'glxy-email-viewer',
  templateUrl: './email-viewer.component.html',
  styleUrls: ['./email-viewer.component.scss'],
  standalone: false,
})
export class EmailViewerComponent implements OnDestroy, OnInit {
  @HostBinding('class') class = 'glxy-email-viewer';

  @Input({ required: true }) html = '';
  @Input({ transform: booleanAttribute }) showDevicePreviewSizeButtons = true;
  @Input({ transform: booleanAttribute }) showEmailInline = false;
  @Input({ transform: booleanAttribute }) openLinksOnNewTabs = false;

  @ViewChild('iframePreview') iframePreview?: ElementRef<HTMLIFrameElement>;

  element = this.elementRef.nativeElement;
  emailPreviewWidth = '100%';
  iframeContentHeight = '';
  showDesktopButton = true;
  showTabletButton = true;
  resizeObserver?: ResizeObserver;
  showDevicePreviewSizeButtonsOnResize = true;
  selectedPreviewSize: 'desktop' | 'tablet' | 'mobile' = 'desktop';

  maxWidthDesktop = `100%`;
  maxWidthTablet = BREAKPOINTS_PX.TABLET_MINIMUM;
  maxWidthMobile = `320px`;

  constructor(private elementRef: ElementRef) {
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = Array.isArray(entries) ? entries[0] : entries;
        const componentWidth = entry.contentRect.width;
        const previousShowDesktop = this.showDesktopButton;
        const previousShowTablet = this.showTabletButton;
        this.showDesktopButton = componentWidth > BREAKPOINTS.TABLET_MINIMUM;
        this.showTabletButton = componentWidth > 400;
        this.showDevicePreviewSizeButtonsOnResize = componentWidth > 0;

        const defaultSize = this.getDefaultPreviewSize();
        const currentInvalid =
          (this.selectedPreviewSize === 'desktop' && !this.showDesktopButton) ||
          (this.selectedPreviewSize === 'tablet' && !this.showTabletButton);
        if (currentInvalid || previousShowDesktop !== this.showDesktopButton || previousShowTablet !== this.showTabletButton) {
          this.setPreviewSize(defaultSize);
        }
      });
      this.resizeObserver.observe(this.element);
    }
  }

  private getDefaultPreviewSize(): 'desktop' | 'tablet' | 'mobile' {
    if (this.showDesktopButton) return 'desktop';
    if (this.showTabletButton) return 'tablet';
    return 'mobile';
  }

  setPreviewSize(size: 'desktop' | 'tablet' | 'mobile'): void {
    this.selectedPreviewSize = size;
    switch (size) {
      case 'desktop':
        this.emailPreviewWidth = this.maxWidthDesktop;
        break;
      case 'tablet':
        this.emailPreviewWidth = this.maxWidthTablet;
        break;
      case 'mobile':
        this.emailPreviewWidth = this.maxWidthMobile;
        break;
    }
  }

  ngOnInit() {
    if (this.openLinksOnNewTabs) {
      this.html = forceOpenTabOnLinks(this.html);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  calculateIframeContentHeight(): void {
    window.setTimeout(() => {
      if (!this.iframePreview || !this.iframePreview.nativeElement.contentWindow) return;
      const iframeBody = this.iframePreview.nativeElement.contentWindow.document.body;
      if (iframeBody) {
        this.iframeContentHeight = iframeBody.offsetHeight + 40 + 'px';
      }
    }, 100);
  }
}

export function forceOpenTabOnLinks(html: string): string {
  return html.replace(/<a\s([^>]*href=["'][^"']*["'][^>]*)>/gi, (match, attrs) => {
    // Check if target or rel are already there
    const hasTarget = /target\s*=/.test(attrs);
    const hasRel = /rel\s*=/.test(attrs);

    const newAttrs = attrs + (hasTarget ? '' : ' target="_blank"') + (hasRel ? '' : ' rel="noopener noreferrer"');

    return `<a ${newAttrs}>`;
  });
}
